import { API_BASE } from "@/lib/api-config";
import { authFetch } from "@/lib/authenticated-fetch";
import { getAccessToken } from "@/lib/local-auth";

export type EmployeeRow = {
  id: string;
  name: string;
  department: string;
  role: string;
  created_at?: string;
  updated_at?: string;
};

/** GET /api/v1/employees — butuh Bearer token dari sesi login. */
export async function fetchEmployees(): Promise<EmployeeRow[]> {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Belum login atau token tidak tersedia.");
  }
  const res = await authFetch(`${API_BASE}/api/v1/employees`, {
    method: "GET",
  });
  const data = (await res.json()) as { data?: EmployeeRow[]; error?: unknown };
  if (!res.ok) {
    const msg = typeof data.error === "string" ? data.error : "Gagal memuat karyawan";
    throw new Error(msg);
  }
  return data.data ?? [];
}

/** POST /api/v1/employees — body { name, department, role } */
export async function createEmployee(body: {
  name: string;
  department: string;
  role: string;
}): Promise<EmployeeRow> {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Belum login atau token tidak tersedia.");
  }
  const res = await authFetch(`${API_BASE}/api/v1/employees`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: body.name,
      department: body.department,
      role: body.role,
    }),
  });
  const data = (await res.json()) as { data?: EmployeeRow; error?: unknown };
  if (!res.ok) {
    const msg = typeof data.error === "string" ? data.error : "Gagal menambah karyawan";
    throw new Error(msg);
  }
  if (!data.data) {
    throw new Error("Respons tidak valid");
  }
  return data.data;
}

/** PATCH /api/v1/employees/:id — body salah satu / beberapa dari name, department, role */
export async function updateEmployee(
  id: string,
  body: Partial<{
    name: string;
    department: string;
    role: string;
  }>,
): Promise<EmployeeRow> {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Belum login atau token tidak tersedia.");
  }
  const res = await authFetch(`${API_BASE}/api/v1/employees/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { data?: EmployeeRow; error?: unknown };
  if (!res.ok) {
    const msg = typeof data.error === "string" ? data.error : "Gagal memperbarui karyawan";
    throw new Error(msg);
  }
  if (!data.data) {
    throw new Error("Respons tidak valid");
  }
  return data.data;
}

/** DELETE /api/v1/employees/:id — 204 tanpa body */
export async function deleteEmployee(id: string): Promise<void> {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Belum login atau token tidak tersedia.");
  }
  const res = await authFetch(`${API_BASE}/api/v1/employees/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (res.status === 204) return;

  const data = (await res.json().catch(() => ({}))) as { error?: unknown };
  const msg = typeof data.error === "string" ? data.error : "Gagal menghapus karyawan";
  throw new Error(msg);
}
